import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { AnnotatedMapWidget, MapAnnotationBase, Theme } from '../../models/reader-document';
import { ensureGoogleMapsLoaded } from '../../utils/google-maps-loader';


@Component({
  selector: 'wr-annotated-map-widget',
  standalone: true,
  template: `
    <section class="px-4">
        <!-- Title / Subtitle -->
        @if (widget()?.title || widget()?.subtitle) {
            <div class="mb-2">
            @if (widget()?.title) {
                <div class="title" [style.color]="titleColor()" [style.font-family]="titleFont()">
                {{ widget()?.title }}
                </div>
            }
            @if (widget()?.subtitle) {
                <div class="subtitle" [style.color]="subtitleColor()" [style.font-family]="subtitleFont()">
                {{ widget()?.subtitle }}
                </div>
            }
            </div>
        }

        <div class="my-2">
        <div
            class="relative w-full overflow-hidden group"
            [style.height.px]="mapHeight()"
            [style.border-radius.px]="cornerRadius()"
            (touchstart)="onTouchStart($event)"
            (touchend)="onTouchEnd($event)"
        >
            <!-- Map host only exists AFTER consent -->
            @if (consentGiven()) {
            <div #mapHost class="absolute inset-0"></div>

                <!-- Arrows (hover only) -->
                @if (layout() === 'horizontal' && (widget()?.annotations?.length ?? 0) > 1) {
                    <button
                    type="button"
                    class="absolute left-2 top-1/2 -translate-y-1/2
                            opacity-0 group-hover:opacity-100 transition-opacity
                            bg-black/35 hover:bg-black/45 text-white
                            rounded-full w-9 h-9 flex items-center justify-center
                            backdrop-blur-sm"
                    (click)="prev()"
                    aria-label="Previous location"
                    >
                    &#10094;
                    </button>
                    <button
                    type="button"
                    class="absolute right-2 top-1/2 -translate-y-1/2
                            opacity-0 group-hover:opacity-100 transition-opacity
                            bg-black/35 hover:bg-black/45 text-white
                            rounded-full w-9 h-9 flex items-center justify-center
                            backdrop-blur-sm"
                    (click)="next()"
                    aria-label="Next location"
                    >
                    &#10095;
                    </button>
                }
            }

            <!-- Consent overlay -->
            @if (!consentGiven()) {
                <div
                    class="absolute inset-0 flex flex-col items-center justify-center gap-3
                        bg-black/40 backdrop-blur-sm text-center px-4"
                    [style.border-radius.px]="cornerRadius()"
                >
                    <div class="text-sm text-white">
                        @if (isGerman()) {
                            Diese Karte wird von Google Maps bereitgestellt. Beim Laden werden Ihre IP-Adresse
                            und Browserdaten an Google übermittelt.
                        } @else {
                            This map is provided by Google Maps. By loading it, your IP address and
                            browser data may be sent to Google.
                        }
                    </div>

                    <button
                    type="button"
                    class="px-4 py-2 rounded-full text-sm font-medium
                            bg-white text-neutral-900 shadow-md hover:bg-neutral-100
                            transition-colors"
                    (click)="enableMap()"
                    >
                        @if (isGerman()) { Karte laden } @else { Load map }
                    </button>

                </div>
            }
        </div>

        @if (layout() === 'horizontal' && (widget()?.annotations?.length ?? 0) > 1 && consentGiven()) {
            <div class="mt-2 flex justify-center">
                <div class="flex gap-2 px-3 py-1.5 rounded-full">
                    @for (a of widget()?.annotations ?? []; track a.id; let idx = $index) {
                    <button
                        type="button"
                        class="w-2.5 h-2.5 rounded-full transition-colors border"
                        [style.background]="idx === activeIndex() ? accentColor() : 'unset'"
                        [style.border-color]="accentColor()"
                        (click)="goTo(idx)"
                        aria-label="Show location"
                    ></button>
                    }
                </div>
            </div>
        }

        @if (errorMsg()) {
            <div class="text-sm opacity-70 mt-2">
                {{ errorMsg() }}
            </div>
        }
        </div>

    </section>
  `,
})
export class AnnotatedMapWidgetComponent {

    consentGiven = signal(false);

    constructor() {
        try {
            const stored = localStorage.getItem('nv_maps_consent');
            if (stored === 'true') {
            this.consentGiven.set(true);
            }
        } catch (e) {
            console.warn('Could not read map consent from storage', e);
        }
    }

    private touchStartX: number | null = null;

    onTouchStart(ev: TouchEvent) {
        if (this.layout() !== 'horizontal') return;
        this.touchStartX = ev.touches?.[0]?.clientX ?? null;
    }

    onTouchEnd(ev: TouchEvent) {
        if (this.layout() !== 'horizontal') return;
        if (this.touchStartX == null) return;

        const endX = ev.changedTouches?.[0]?.clientX ?? null;
        if (endX == null) return;

        const dx = endX - this.touchStartX;
        this.touchStartX = null;

        const threshold = 40;

        if (dx > threshold) this.prev();     // right
        else if (dx < -threshold) this.next(); // left
    }
    private _mapHost?: ElementRef<HTMLDivElement>;

    @ViewChild('mapHost')
    set mapHost(el: ElementRef<HTMLDivElement> | undefined) {
        this._mapHost = el;

        // Only init when:
        // - host exists
        // - user has given consent
        // - we actually have widget data
        if (el && this.consentGiven() && this.widget()) {
            this.errorMsg.set(null);

            ensureGoogleMapsLoaded()
            .then(() => this.initOrUpdateMap(el.nativeElement, this.widget()!))
            .catch((err) => {
                console.warn(err);
                this.errorMsg.set(err?.message ?? 'Map could not be loaded.');
            });
        }
    }

    get mapHost(): ElementRef<HTMLDivElement> | undefined {
        return this._mapHost;
    }

    private widgetSig = signal<AnnotatedMapWidget | null>(null);
    private themeSig = signal<(Theme & { isDark?: boolean; googleMapsApiKey?: string }) | null>(null);

    private activeIndexSig = signal<number>(0);
    errorMsg = signal<string | null>(null);

    // Google Maps objects
    private map: google.maps.Map | null = null;
    private infoWindow: google.maps.InfoWindow | null = null;
    private currentMarker: google.maps.marker.AdvancedMarkerElement | google.maps.Marker | null = null;
    private currentOverlay: google.maps.Polygon | google.maps.Polyline | null = null;

    @Input() set json(value: AnnotatedMapWidget) {
        this.widgetSig.set(value);
        this.activeIndexSig.set(0);
    }

    @Input() set theme(value: Theme | undefined) {
        // optionally allow host to pass api key via theme
        this.themeSig.set((value as any) ?? null);
    }

    widget = computed(() => this.widgetSig());
    activeIndex = computed(() => this.activeIndexSig());

    layout = computed(() => this.widget()?.layout ?? 'singleMap');

    // style knobs
    mapHeight = computed(() => 250);
    cornerRadius = computed(() => (this.themeSig()?.cornerRadius ?? 0));

    private colors = computed(() => {
        const t = this.themeSig();
        if (!t) return null;
        return t.isDark ? t.darkColors : t.lightColors;
    });

    accentColor = computed(() => this.colors()?.accent ?? '#FF5E38');

    titleColor = computed(() => this.colors()?.title ?? this.colors()?.primary ?? '#111827');
    subtitleColor = computed(() => this.colors()?.subtitle ?? this.colors()?.secondary ?? '#4b5563');

    titleFont = computed(() => this.themeSig()?.fonts?.title?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
    subtitleFont = computed(() => this.themeSig()?.fonts?.subtitle?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');

    isGerman = computed(() => navigator.language?.startsWith('de'));

    // navigation

    prev() {
        const len = this.widget()?.annotations?.length ?? 0;
        if (len <= 1) return;
        this.activeIndexSig.set((this.activeIndexSig() - 1 + len) % len);
        this.renderActiveAnnotation();
    }

    next() {
        const len = this.widget()?.annotations?.length ?? 0;
        if (len <= 1) return;
        this.activeIndexSig.set((this.activeIndexSig() + 1) % len);
        this.renderActiveAnnotation();
    }

    goTo(idx: number) {
        this.activeIndexSig.set(idx);
        this.renderActiveAnnotation();
    }

    // map render

    private initOrUpdateMap(host: HTMLDivElement, w: AnnotatedMapWidget) {
        const mapTypeId = (w.style === 'satellite' ? 'satellite' : 'roadmap') as google.maps.MapTypeId;

        // initial center from first annotation
        const first = w.annotations?.[0];
        const center = this.getLatLng(first) ?? { lat: 46.83310649280406, lng: 2.9504472409971418 };

        if (!this.map) {
        this.map = new google.maps.Map(host, {
            zoom: w.zoom ?? 4,
            center,
            mapTypeId,
            disableDefaultUI: true,
        });
        this.infoWindow = new google.maps.InfoWindow();
        } else {
        this.map.setMapTypeId(mapTypeId);
        this.map.setCenter(center);
        this.map.setZoom(w.zoom ?? 4);
        }

        if (this.layout() === 'singleMap') {
        this.renderAllAnnotations();
        } else {
        this.renderActiveAnnotation();
        }
    }

    private renderAllAnnotations() {
        const w = this.widget();
        const map = this.map;
        if (!w || !map) return;

        const annotations = w.annotations ?? [];
        if (!annotations.length) return;

        this.clearCurrentOverlays(true);

        annotations.forEach((a, idx) => this.addAnnotationToMap(a, idx, map, true));

        if (annotations.length <= 1) {
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        let hasBounds = false;

        for (const a of annotations) {
            const type = a.type ?? 'point';

            if (type === 'polygon' || type === 'line') {
            const path = this.decodePairs(a.encodedContent);
            for (const pt of path) {
                bounds.extend(pt);
                hasBounds = true;
            }
            } else {
            const pos = this.getLatLng(a);
            if (pos) {
                bounds.extend(pos);
                hasBounds = true;
            }
            }
        }

        if (hasBounds) {
            map.fitBounds(bounds, 32);
        }
    }

    private renderActiveAnnotation() {
        const w = this.widget();
        const map = this.map;
        if (!w || !map) return;

        this.clearCurrentOverlays(true);

        const idx = this.activeIndexSig();
        const a = w.annotations?.[idx];
        if (!a) return;

        this.addAnnotationToMap(a, idx, map, false);
    }

    private clearCurrentOverlays(clearEverything = false) {
        if (this.currentMarker) {
            // AdvancedMarkerElement has map property; Marker has setMap
            try {
                (this.currentMarker as any).map = null;
            } catch {}
            try {
                (this.currentMarker as any).setMap?.(null);
            } catch {}
            this.currentMarker = null;
        }

        if (this.currentOverlay) {
            this.currentOverlay.setMap(null);
            this.currentOverlay = null;
        }

        if (clearEverything && this.infoWindow) {
            this.infoWindow.close();
        }
    }

    private addAnnotationToMap(
        a: MapAnnotationBase,
        idx: number,
        map: google.maps.Map,
        keepExisting: boolean
        ) {
        const type = a.type ?? 'point';

        if (type === 'polygon') {
            const path = this.decodePairs(a.encodedContent);
            if (!path.length) return;

            const poly = new google.maps.Polygon({
            paths: path,
            fillColor: a.fillColor ?? '#FF5E38',
            fillOpacity: 0.2,
            strokeColor: a.strokeColor ?? '#FF5E38',
            strokeOpacity: 1,
            strokeWeight: a.lineWidth ?? 2,
            });

            poly.setMap(map);
            if (!keepExisting) this.currentOverlay = poly;

            map.setCenter(path[0]);
            map.setZoom(9);
            return;
        }

        if (type === 'line') {
            const path = this.decodePairs(a.encodedContent);
            if (!path.length) return;

            const line = new google.maps.Polyline({
            path,
            strokeColor: a.strokeColor ?? '#FF5E38',
            strokeOpacity: 1,
            strokeWeight: a.lineWidth ?? 3,
            });

            line.setMap(map);
            if (!keepExisting) this.currentOverlay = line;

            map.setCenter(path[0]);
            map.setZoom(11);
            return;
        }

        // point
        const pos = this.getLatLng(a);
        if (!pos) return;

        const badge = a.badge ?? String(idx + 1);
        const pinBg = a.pinBackgroundColor ?? a.pinColor ?? this.accentColor();
        const pinFg = a.pinForegroundColor ?? '#000000';

        const content = this.buildInfoWindowHtml(a, badge);

        // Prefer AdvancedMarkerElement if available
        const g = (window as any).google;
        const hasAdvanced = !!g?.maps?.marker?.AdvancedMarkerElement;

        if (hasAdvanced) {
            const iconDiv = document.createElement('div');
            iconDiv.innerHTML = this.pinSvg(badge, pinBg, pinFg);

            const adv = new g.maps.marker.AdvancedMarkerElement({
            map,
            position: pos,
            content: iconDiv,
            title: a.title ?? a.name ?? `annotation_${idx + 1}`,
            });

            adv.addListener('click', () => {
            this.openInfoWindow(content, adv);
            });

            if (!keepExisting) this.currentMarker = adv;
        } else {
            // Fallback
            const svg = this.pinSvg(badge, pinBg, pinFg);
            const svgUrl =
            'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

            const marker = new google.maps.Marker({
            map,
            position: pos,
            title: a.title ?? a.name ?? `annotation_${idx + 1}`,
            icon: {
                url: svgUrl,
                scaledSize: new google.maps.Size(36, 42),
                anchor: new google.maps.Point(18, 42), // bottom middle
            },
            });

            marker.addListener('click', () => {
            this.openInfoWindow(content, marker);
            });

            if (!keepExisting) this.currentMarker = marker;
        }

        map.setCenter(pos);
        map.setZoom(11);
    }

    private openInfoWindow(html: string, anchor: any) {
        if (!this.map || !this.infoWindow) return;

        this.infoWindow.setContent(html);
        this.infoWindow.open({
        anchor,
        map: this.map,
        });
    }

    private getLatLng(a?: MapAnnotationBase | null): google.maps.LatLngLiteral | null {
        if (!a) return null;

        if (typeof a.lat === 'number' && typeof a.lng === 'number') {
            return { lat: a.lat, lng: a.lng };
        }

        if (Array.isArray(a.coordinate) && a.coordinate.length === 2) {
            return { lat: a.coordinate[0], lng: a.coordinate[1] };
        }

        return null;
    }

    private decodePairs(encoded?: string): google.maps.LatLngLiteral[] {
        if (!encoded) return [];
        const parts = encoded.split(',').map((v) => v.trim()).filter(Boolean);
        const out: google.maps.LatLngLiteral[] = [];

        for (let i = 0; i < parts.length - 1; i += 2) {
            const lat = Number(parts[i]);
            const lng = Number(parts[i + 1]);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                out.push({ lat, lng });
            }
        }
        return out;
    }

    private directionLink(a: MapAnnotationBase): string | null {
        const pos = this.getLatLng(a);
        if (!pos) return null;
        // old linkdirectionMap
        return `https://www.google.com/maps/dir//${pos.lat},${pos.lng}/@${pos.lat},${pos.lng}`;
    }

    private buildInfoWindowHtml(a: MapAnnotationBase, badge: string): string {
        const title = a.title ?? a.name ?? '';
        const subtitle = a.subtitle ?? '';
        const link = a.linkURL ?? '';
        const dir = a.directions ? this.directionLink(a) : null;

        // keep it small + safe-ish; you already render HTML in old version
        const accent = this.accentColor();

        const esc = (s: string) =>
        s.replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#039;');

        let html = `<div style="display:flex;flex-direction:column;align-items:flex-start;gap:6px;">`;

        if (title) html += `<div style="font-size:16px;word-break:break-word;">${esc(title)}</div>`;
        if (subtitle) html += `<div style="word-break:break-word;opacity:.9;">${esc(subtitle)}</div>`;

        if (link) {
            html += `<a href="${esc(link)}" target="_blank" rel="noopener noreferrer" style="color:${accent};text-decoration:underline;">Link</a>`;
        }

        if (!title && !subtitle && !link) {
            html += `<div style="opacity:.7;">${esc(badge)}</div>`;
        }

        if (dir) {
            html += `<a href="${dir}" target="_blank" rel="noopener noreferrer" style="margin-top:6px;">
                <button style="background:rgba(182,182,183,.6);border:none;border-radius:${this.cornerRadius()}px;padding:6px 10px;color:#4f4f4f;cursor:pointer;">
                Route
                </button>
            </a>`;
        }

        html += `</div>`;
        return html;
    }

    enableMap() {
        this.consentGiven.set(true);

        try {
            localStorage.setItem('nv_maps_consent', 'true');
        } catch (e) {
            console.warn('Could not persist map consent', e);
        }
    }

    private pinSvg(text: string, bg: string, fg: string): string {
        const raw = text ?? '';
        const len = raw.length;

        const maxChars = 5;
        const displayText =
            len > maxChars ? raw.slice(0, maxChars - 1) + '…' : raw;

        // size based on length
        let fontSize = 70; // 1–2 chars
        if (len === 3) {
            fontSize = 56;
        } else if (len === 4) {
            fontSize = 48;
        } else if (len >= 5) {
            fontSize = 42;
        }

        return `
            <svg width="36" height="42" viewBox="0 0 146 169" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M73,0 C113.316787,0 146,32.6832133 146,73 C146,105.660995 124.550767,133.312254 94.9700872,142.635991 L73,169 L51.0309086,142.636305 C21.449714,133.312875 0,105.661362 0,73 C0,32.6832133 32.6832133,0 73,0 Z"
                fill="${bg}"
                stroke-width="2"
            />
            <text
                text-anchor="middle"
                x="50%"
                y="48%"
                font-size="${fontSize}"
                fill="#fff"
                font-family="Arial, sans-serif"
                font-weight="600"
                dominant-baseline="middle"
            >
                ${displayText}
            </text>
            </svg>
        `;
    }

}
